import React, { useState, useEffect, useContext, useCallback } from 'react';

import { useTranslation } from 'react-i18next';
// removed useQueryClient as we refresh locally and via optional refetch prop
import Modal from 'react-bootstrap/Modal';
import 'bootstrap/dist/css/bootstrap.min.css';

import Textarea from '../../components/Form/Textarea';
import Button from '../../components/Button';

import { UiContext } from '../../context/UiContext';
import classes from './styles.module.scss';
import AdminService from '../../services/AdminService';

export default function AddCompanyNoteModal({
  show,
  handleClose,
  currentCompany,
  refetch,
}) {
  const [note, setNote] = useState('');
  const [companyNotes, setCompanyNotes] = useState([]);
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);

  const { setIsFetching } = useContext(UiContext);

  const { t } = useTranslation();

  const refreshNotes = useCallback(async () => {
    if (!currentCompany?.id) return;
    try {
      setIsLoadingNotes(true);
      const data = await AdminService.getCompanyNotes({
        id: currentCompany.id,
      });
      const list = Array.isArray(data) ? data : data?.items || [];
      setCompanyNotes(list);
    } catch (error) {
      // noop
    } finally {
      setIsLoadingNotes(false);
    }
  }, [currentCompany?.id]);

  const addNote = async () => {
    try {
      setIsFetching(true);
      await AdminService.addNoteToCompany({
        id: currentCompany.id,
        note,
      });
      await refreshNotes();
      if (refetch) await refetch();
      setNote('');
    } catch (error) {
      // noop
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    if (show && currentCompany?.id) {
      refreshNotes();
    }

    return () => {
      setCompanyNotes([]);
      setNote('');
    };
  }, [show, currentCompany, refreshNotes]);

  const formatDateTime = (dateStr) => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleString();
    } catch (_) {
      return '';
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      className={classes.AddCandidateNoteModal}
      backdropClassName={classes.backdrop}
      contentClassName={classes.modalContent}
      dialogClassName={classes.dialog}
    >
      <div className={classes.content}>
        {/* Existing notes list */}
        {isLoadingNotes ? null : (
          <div className={classes.notesList}>
            {companyNotes?.length > 0 ? (
              companyNotes.map((n) => {
                const author = `${n?.user?.userProfile?.firstName || ''}${
                  n?.user?.userProfile?.lastName
                    ? ` ${n.user.userProfile.lastName}`
                    : ''
                }`;
                return (
                  <div className={classes.noteItem} key={n?.id || n?.createdAt}>
                    <div className={classes.noteText}>{n?.note || ''}</div>
                    <div className={classes.noteMeta}>
                      <span className={classes.author}>{author}</span>
                      {n?.createdAt ? (
                        <>
                          <span className={classes.separator}>/</span>
                          <span className={classes.date}>
                            {formatDateTime(n.createdAt)}
                          </span>
                        </>
                      ) : null}
                    </div>
                  </div>
                );
              })
            ) : (
              <div />
            )}
          </div>
        )}
        <div className={classes.textareaContainer}>
          <Textarea
            note={t('modals.AddCandidateNoteModal.optionalNote')}
            width={455}
            height={145}
            placeholder={t('modals.AddCandidateNoteModal.enterNotesHere')}
            maxLength={250}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </div>
        <div className={classes.buttons}>
          <Button
            width={150}
            height={50}
            onClick={handleClose}
            variant="secondary"
          >
            {t('modals.AddCandidateNoteModal.cancel')}
          </Button>
          <Button width={150} height={50} disabled={!note} onClick={addNote}>
            {t('modals.AddCandidateNoteModal.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
